/**
 * PortalLandingPage - Unified entry point for versicherten portal
 *
 * Presents two equal-hierarchy paths:
 * 1. Login (Anmelden) - Access code entry for existing appointments
 * 2. Registration (Registrieren) - Self-service registration for new patients
 *
 * Both paths are presented as equal tabs, replacing the previous flow where
 * login was primary and registration was a secondary link.
 */

import { useState, type FormEvent } from "react";
import { useNavigate, useSearch, Link } from "@tanstack/react-router";
import { useAction, useMutation } from "convex/react";
import { useTranslation } from "react-i18next";
import { api } from "@jourfx/convex/client";
import { AccessError } from "../components/access/AccessError";
import { useSession } from "../hooks/useSession";
import { useVersichertenSession } from "../hooks/useVersichertenSession";
import {
  HelpCircle,
  ArrowRight,
  Loader2,
  LogIn,
  UserPlus,
  CheckCircle2,
} from "lucide-react";
import { PrivacyNoticeModal } from "../components/PrivacyNoticeModal";
import { useBrandLogoUrl } from "@/branding/BrandingProvider";
import { BirthdayPicker } from "@/components/BirthdayPicker";
import { SELF_REGISTRATION_REASONS } from "@/lib/selfRegistration";

// ─── Types ──────────────────────────────────────────────────────────

type ActiveTab = "login" | "register";

type ErrorType =
  | "invalid"
  | "locked_temp"
  | "locked_perm"
  | "expired"
  | "not_found"
  | "appointment_invalid"
  | "server_error"
  | "rate_limited";

type ErrorState = {
  type: ErrorType;
  remainingAttempts?: number;
  unlockAt?: number;
  retryAfter?: number;
} | null;

// ─── Registration constants ─────────────────────────────────────────

const VIDEO_CONSENT_URL =
  import.meta.env.VITE_REGISTRATION_VIDEO_CONSENT_URL ||
  "/legal/einwilligungserklaerung-videosprechstunde.pdf";
const ART13_DSGVO_URL =
  import.meta.env.VITE_REGISTRATION_ART13_DSGVO_URL ||
  "/legal/hinweise-art-13-dsgvo.pdf";
type RegistrationState = {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  phone: string;
  email: string;
  begutachtungsGrund: string;
  hasAcceptedEinwilligung: boolean;
  hasAcceptedArt13Dsgvo: boolean;
  isHumanConfirmed: boolean;
};

const initialRegistrationState: RegistrationState = {
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  phone: "",
  email: "",
  begutachtungsGrund: "",
  hasAcceptedEinwilligung: false,
  hasAcceptedArt13Dsgvo: false,
  isHumanConfirmed: false,
};

// ─── Main Component ─────────────────────────────────────────────────

export function PortalLandingPage() {
  const { t } = useTranslation("portalAccess");
  const { t: tc } = useTranslation("common");
  const primaryLogoUrl = useBrandLogoUrl("primary");
  const search = useSearch({ from: "/" });
  const navigate = useNavigate();

  const searchParams = search as {
    id?: string;
    code?: string;
    next?: string;
    tab?: string;
  };

  // Auto-select login tab if access code or appointment ID is provided
  const hasPrefilledCredentials =
    (searchParams.code?.trim().length ?? 0) > 0 ||
    (searchParams.id?.trim().length ?? 0) > 0;

  const [activeTab, setActiveTab] = useState<ActiveTab>(
    searchParams.tab === "register" && !hasPrefilledCredentials
      ? "register"
      : "login"
  );

  const [privacyAccepted, setPrivacyAccepted] = useState(() => {
    try {
      return localStorage.getItem("jourfx-privacy-accepted") === "1";
    } catch {
      return false;
    }
  });

  const imprintLabel = tc("footer.imprint");
  const privacyPolicyLabel = tc("footer.privacyPolicy");

  return (
    <div className="min-h-screen client-gradient-bg flex flex-col">
      {/* Header with Logo */}
      <header className="p-4 sm:p-6">
        <img
          src={primaryLogoUrl}
          alt="JourFx"
          className="h-8 sm:h-10 lg:h-12 w-auto"
        />
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-start sm:items-center justify-center p-4 sm:p-6">
        <div
          className={`w-full transition-all duration-300 ${
            activeTab === "register" ? "max-w-2xl" : "max-w-md"
          }`}
        >
          {/* Tab Selector */}
          <div className="flex rounded-2xl bg-[var(--stone-100)] p-1.5 mb-6">
            <button
              type="button"
              onClick={() => setActiveTab("login")}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-base font-semibold transition-all ${
                activeTab === "login"
                  ? "bg-white text-[var(--stone-800)] shadow-sm"
                  : "text-[var(--stone-500)] hover:text-[var(--stone-700)]"
              }`}
            >
              <LogIn className="w-4.5 h-4.5" />
              Anmelden
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("register")}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-base font-semibold transition-all ${
                activeTab === "register"
                  ? "bg-white text-[var(--stone-800)] shadow-sm"
                  : "text-[var(--stone-500)] hover:text-[var(--stone-700)]"
              }`}
            >
              <UserPlus className="w-4.5 h-4.5" />
              Registrieren
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === "login" ? (
            <LoginForm
              searchParams={searchParams}
              navigate={navigate}
              t={t}
              tc={tc}
            />
          ) : (
            <RegistrationForm
              onSwitchToLogin={() => setActiveTab("login")}
            />
          )}
        </div>
      </main>

      {/* Legal footer */}
      <footer className="client-footer relative z-10">
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
          <Link to="/datenschutz" className="client-footer-link">
            {privacyPolicyLabel}
          </Link>
          <span className="text-[var(--stone-300)]" aria-hidden="true">
            |
          </span>
          <Link to="/impressum" className="client-footer-link">
            {imprintLabel}
          </Link>
        </div>
      </footer>

      {/* Privacy Notice Modal */}
      {!privacyAccepted && (
        <PrivacyNoticeModal
          onAccept={() => {
            setPrivacyAccepted(true);
            try {
              localStorage.setItem("jourfx-privacy-accepted", "1");
            } catch {}
          }}
          onDecline={() => navigate({ to: "/declined" })}
        />
      )}
    </div>
  );
}

// ─── Login Form ─────────────────────────────────────────────────────

function LoginForm({
  searchParams,
  navigate,
  t,
  tc,
}: {
  searchParams: { id?: string; code?: string; next?: string };
  navigate: ReturnType<typeof useNavigate>;
  t: (key: string) => string;
  tc: (key: string) => string;
}) {
  const { createSession, clearSession } = useSession();
  const { createSession: createVersichertenSession } =
    useVersichertenSession();
  const apiRef = api as any;
  const loginPortal = useAction(apiRef.auth.portal.login);

  const initialCode = searchParams.code || "";
  const initialId = searchParams.id || "";
  const nextAfterLogin = searchParams.next || "";
  const hasAccessCode = initialCode.trim().length > 0;
  const isAppointmentIdPrefilled =
    hasAccessCode || initialId.trim().length > 0;

  const [accessCode, setAccessCode] = useState(initialCode);
  const [appointmentId] = useState(initialId);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<ErrorState>(null);

  const SESSION_TTL_FALLBACK_MS = 4 * 60 * 60 * 1000;

  const toStringValue = (value: unknown): string | null => {
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      const normalized =
        value > 0 && value < 1_000_000_000_000 ? value * 1000 : value;
      if (Number.isFinite(normalized)) return String(normalized);
    }
    return null;
  };

  const toSessionExpiresAt = (value: unknown, now = Date.now()): number => {
    if (typeof value === "number" && Number.isFinite(value)) {
      const normalized =
        value > 0 && value < 1_000_000_000_000 ? value * 1000 : value;
      return normalized > now ? normalized : now + SESSION_TTL_FALLBACK_MS;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      const parsed = Number(trimmed);
      if (Number.isFinite(parsed)) {
        const normalized =
          parsed > 0 && parsed < 1_000_000_000_000
            ? parsed * 1000
            : parsed;
        return normalized > now ? normalized : now + SESSION_TTL_FALLBACK_MS;
      }
      const parsedDate = Date.parse(trimmed);
      if (!Number.isNaN(parsedDate)) {
        return parsedDate > now ? parsedDate : now + SESSION_TTL_FALLBACK_MS;
      }
    }
    return now + SESSION_TTL_FALLBACK_MS;
  };

  const resolveSafeNextPath = (value: string): string | null => {
    const normalized = value.trim();
    if (!normalized.startsWith("/") || normalized.startsWith("//")) return null;
    return normalized;
  };

  const handleSubmit = async () => {
    const hasCode = accessCode.trim().length > 0;
    const hasId = appointmentId.trim().length > 0;
    if (!hasCode && !hasId) {
      setError({ type: "appointment_invalid" });
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      const result = await loginPortal({
        ...(hasCode
          ? { accessCode: accessCode.trim() }
          : { appointmentId: appointmentId.trim() as any }),
        userAgent: navigator.userAgent,
      });

      if (result.valid) {
        const versichertenProfileId =
          (result as any).versichertenProfileId ??
          (result as any).patientProfileId;
        const appointmentFlowAppointmentId = (result as any).appointmentId;
        const sessionToken = toStringValue((result as any).sessionToken);
        const resolvedVersicherterProfileId =
          toStringValue(versichertenProfileId);
        const resolvedAppointmentId = toStringValue(
          appointmentFlowAppointmentId
        );
        const expiresAt = toSessionExpiresAt(
          (result as any).expiresAt,
          Date.now()
        );

        // Versicherten profile flow → dashboard
        if (
          (result as any).flow === "versicherter" ||
          (!((result as any).flow as string | undefined) &&
            !!resolvedVersicherterProfileId &&
            !!sessionToken)
        ) {
          clearSession();
          if (!sessionToken || !resolvedVersicherterProfileId) {
            setError({ type: "server_error" });
            return;
          }
          createVersichertenSession(
            resolvedVersicherterProfileId,
            sessionToken,
            expiresAt
          );
          const safeNextPath = resolveSafeNextPath(nextAfterLogin);
          if (safeNextPath) {
            navigate({ to: safeNextPath } as any);
            return;
          }
          navigate({ to: "/dashboard" } as any);
          return;
        }

        // Appointment flow
        if (
          (result as any).flow === "appointment" ||
          (!((result as any).flow as string | undefined) &&
            !!resolvedAppointmentId &&
            !!sessionToken)
        ) {
          if (!sessionToken || !resolvedAppointmentId) {
            setError({ type: "server_error" });
            return;
          }
          createSession(resolvedAppointmentId, expiresAt, sessionToken);
          navigate({
            to: "/appointment/$appointmentId",
            params: { appointmentId: resolvedAppointmentId },
          });
          return;
        }

        setError({ type: "server_error" });
        return;
      } else {
        const errorMap: Record<string, ErrorType> = {
          ACCESS_CODE_INVALID: "invalid",
          ACCESS_CODE_EXPIRED: "expired",
          ACCESS_CODE_NOT_FOUND: "not_found",
          ACCESS_LINK_USED: "expired",
          APPOINTMENT_NOT_FOUND: "appointment_invalid",
          BOOKING_SESSION_EXPIRED: "expired",
          RATE_LIMITED: "rate_limited",
        };

        setError({
          type: errorMap[result.errorCode] || "invalid",
          remainingAttempts: result.remainingAttempts,
          unlockAt: result.unlockAt,
          retryAfter: result.retryAfter,
        });
      }
    } catch (err) {
      console.error("Access code validation error:", err);
      setError({ type: "server_error" });
    } finally {
      setIsValidating(false);
    }
  };

  const isLocked =
    error?.type === "locked_temp" || error?.type === "locked_perm";
  const hasIdentifier =
    accessCode.trim().length > 0 || appointmentId.trim().length > 0;
  const canSubmit = hasIdentifier && !isValidating && !isLocked;

  return (
    <>
      <div className="client-card p-6 sm:p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[var(--stone-800)] mb-3">
            {t("title")}
          </h1>
          <p className="text-[var(--stone-600)] text-base sm:text-lg leading-relaxed">
            {t("subtitle")}
          </p>
        </div>

        {/* Access Code Input */}
        <div className="mb-6">
          <label
            htmlFor="access-code"
            className="block text-sm font-medium text-[var(--stone-700)] mb-2"
          >
            {t("form.accessCode.label")}
          </label>
          <input
            id="access-code"
            type="text"
            value={accessCode}
            onChange={(e) => {
              setAccessCode(e.target.value.toUpperCase());
              if (error) setError(null);
            }}
            placeholder={t("form.accessCode.placeholder")}
            disabled={isValidating || isLocked || isAppointmentIdPrefilled}
            className={`w-full px-4 py-3 text-lg rounded-xl border-2 bg-white transition-colors tracking-widest font-mono
              ${
                error?.type === "appointment_invalid"
                  ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                  : "border-[var(--stone-200)] focus:border-[var(--sage-500)] focus:ring-[var(--sage-100)]"
              }
              focus:outline-none focus:ring-4
              disabled:bg-[var(--stone-100)] disabled:text-[var(--stone-400)] disabled:cursor-not-allowed
              placeholder:text-[var(--stone-400)]`}
            aria-describedby={
              error?.type === "appointment_invalid"
                ? "access-code-error"
                : undefined
            }
          />
          {error?.type === "appointment_invalid" && (
            <p id="access-code-error" className="mt-2 text-sm text-red-600">
              {t("form.accessCode.error")}
            </p>
          )}
          <p className="mt-2 text-sm text-[var(--stone-500)]">
            {t("form.accessCode.hint")}
          </p>
        </div>

        {/* Error State */}
        {error &&
          error.type !== "appointment_invalid" &&
          error.type !== "server_error" && (
            <div className="mb-6">
              <AccessError
                type={error.type}
                remainingAttempts={error.remainingAttempts}
                unlockAt={error.unlockAt}
              />
            </div>
          )}

        {/* Server Error State with Dev Bypass */}
        {error?.type === "server_error" && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-amber-800 font-medium mb-2">
              {tc("errors.network")}
            </p>
            <p className="text-amber-700 text-sm mb-4">
              {tc("errors.serverUnavailable")}
            </p>
            {import.meta.env.DEV && (
              <button
                type="button"
                onClick={() =>
                  navigate({
                    to: "/tech-check/$appointmentId",
                    params: {
                      appointmentId:
                        accessCode.trim() || appointmentId.trim(),
                    },
                  })
                }
                className="w-full px-4 py-2 text-sm font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors"
              >
                {t("devMode.continue")}
              </button>
            )}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold text-lg transition-all
            ${
              canSubmit
                ? "bg-[var(--sage-600)] text-white hover:bg-[var(--sage-700)] active:scale-[0.98]"
                : "bg-[var(--stone-200)] text-[var(--stone-400)] cursor-not-allowed"
            }`}
        >
          {isValidating ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {t("validating")}
            </>
          ) : (
            <>
              {t("form.submit")}
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>

      {/* Help link */}
      <div className="mt-6">
        <a
          href={`tel:${String(tc("support.phone")).replace(/[^\d+]/g, "")}`}
          className="flex items-center justify-center gap-2 text-sm text-[var(--stone-500)] hover:text-[var(--sage-600)] transition-colors"
        >
          <HelpCircle className="w-4 h-4" />
          {t("form.helpLink")}
        </a>
      </div>
    </>
  );
}

// ─── Registration Form ──────────────────────────────────────────────

function RegistrationForm({
  onSwitchToLogin,
}: {
  onSwitchToLogin: () => void;
}) {
  const apiRef = api as any;
  const registerSelfService = useMutation(
    apiRef.versichertenProfiles.index.registerSelfService
  );

  const [form, setForm] = useState<RegistrationState>(
    initialRegistrationState
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successEmail, setSuccessEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const toAbsoluteUrl = (url: string) => {
    try {
      return new URL(url, window.location.origin).toString();
    } catch {
      return url;
    }
  };

  const updateField = <K extends keyof RegistrationState>(
    key: K,
    value: RegistrationState[K]
  ) => {
    setForm((current) => ({ ...current, [key]: value }));
    if (errorMessage) setErrorMessage(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSuccess(false);

    if (!form.dateOfBirth) {
      setErrorMessage("Bitte geben Sie Ihr Geburtsdatum an.");
      return;
    }

    if (!form.hasAcceptedEinwilligung || !form.hasAcceptedArt13Dsgvo) {
      setErrorMessage(
        "Bitte bestätigen Sie beide Dokumente, bevor Sie sich registrieren."
      );
      return;
    }

    if (!form.isHumanConfirmed) {
      setErrorMessage(
        "Bitte bestätigen Sie die Anti-Roboter-Verifizierung."
      );
      return;
    }

    setIsSubmitting(true);

    try {
      await registerSelfService({
        firstName: form.firstName,
        lastName: form.lastName,
        dateOfBirth: form.dateOfBirth,
        phone: form.phone.trim() || undefined,
        email: form.email,
        begutachtungsGrund: form.begutachtungsGrund,
        hasAcceptedEinwilligung: form.hasAcceptedEinwilligung,
        hasAcceptedArt13Dsgvo: form.hasAcceptedArt13Dsgvo,
        einwilligungVideoUrl: toAbsoluteUrl(VIDEO_CONSENT_URL),
        art13DsgvoUrl: toAbsoluteUrl(ART13_DSGVO_URL),
        userAgent: navigator.userAgent,
      });

      setSuccessEmail(form.email);
      setForm(initialRegistrationState);
      setErrorMessage(null);
      setIsSuccess(true);
    } catch (error) {
      console.error("Self registration failed", error);
      setErrorMessage(
        "Die Registrierung konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    "h-12 w-full rounded-lg border-2 border-[var(--stone-200)] bg-white px-4 text-base outline-none transition-colors focus:border-[var(--sage-500)] focus:ring-4 focus:ring-[var(--sage-500)]/15";

  if (isSuccess) {
    return (
      <div className="client-card p-8 sm:p-10 text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--sage-100)]">
          <CheckCircle2 className="h-8 w-8 text-[var(--sage-600)]" />
        </div>
        <h1 className="text-2xl font-bold text-[var(--stone-800)]">
          Registrierung eingegangen
        </h1>
        <p className="mt-4 text-[var(--stone-600)] leading-relaxed">
          Vielen Dank. Wir haben Ihre Daten erhalten und senden Ihnen in Kürze
          eine Bestätigungs-E-Mail an{" "}
          <strong>{successEmail || "Ihre E-Mail-Adresse"}</strong>. Bitte
          prüfen Sie auch Ihren Spam-Ordner.
        </p>
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="client-btn-secondary mt-8 inline-flex"
        >
          Zur Anmeldung
        </button>
      </div>
    );
  }

  return (
    <div className="client-card p-6 sm:p-8">
      <h1 className="text-2xl sm:text-3xl font-bold text-[var(--stone-800)]">
        Registrierung
      </h1>
      <p className="mt-3 text-[var(--stone-600)] leading-relaxed">
        Füllen Sie das Formular aus, um sich für eine Videobegutachtung
        anzumelden. Sie erhalten anschließend eine Bestätigungs-E-Mail.
      </p>

      {errorMessage && (
        <div className="mt-6 rounded-lg border-2 border-[oklch(0.85_0.10_25)] bg-[oklch(0.97_0.02_25)] px-4 py-3 text-[oklch(0.40_0.08_25)]">
          {errorMessage}
        </div>
      )}

      <form className="mt-8" onSubmit={handleSubmit}>
        {/* --- Section: Persönliche Daten --- */}
        <fieldset className="space-y-4">
          <legend className="text-sm font-semibold uppercase tracking-wider text-[var(--stone-500)]">
            Persönliche Daten
          </legend>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label
                htmlFor="registration-first-name"
                className="text-base font-medium text-[var(--stone-700)]"
              >
                Vorname
              </label>
              <input
                id="registration-first-name"
                required
                value={form.firstName}
                onChange={(e) => updateField("firstName", e.target.value)}
                className={inputClass}
              />
            </div>
            <div className="space-y-1.5">
              <label
                htmlFor="registration-last-name"
                className="text-base font-medium text-[var(--stone-700)]"
              >
                Nachname
              </label>
              <input
                id="registration-last-name"
                required
                value={form.lastName}
                onChange={(e) => updateField("lastName", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label
              htmlFor="registration-date-of-birth"
              className="text-base font-medium text-[var(--stone-700)]"
            >
              Geburtsdatum
            </label>
            <BirthdayPicker
              id="registration-date-of-birth"
              value={form.dateOfBirth}
              onChange={(date) => updateField("dateOfBirth", date)}
              className={inputClass}
            />
          </div>
        </fieldset>

        {/* --- Section: Kontakt --- */}
        <fieldset className="mt-8 space-y-4">
          <legend className="text-sm font-semibold uppercase tracking-wider text-[var(--stone-500)]">
            Kontaktdaten
          </legend>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label
                htmlFor="registration-phone"
                className="text-base font-medium text-[var(--stone-700)]"
              >
                Telefonnummer
              </label>
              <input
                id="registration-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => updateField("phone", e.target.value)}
                className={inputClass}
              />
            </div>
            <div className="space-y-1.5">
              <label
                htmlFor="registration-email"
                className="text-base font-medium text-[var(--stone-700)]"
              >
                E-Mail-Adresse
              </label>
              <input
                id="registration-email"
                required
                type="email"
                value={form.email}
                onChange={(e) => updateField("email", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
        </fieldset>

        {/* --- Section: Begutachtung --- */}
        <fieldset className="mt-8 space-y-4">
          <legend className="text-sm font-semibold uppercase tracking-wider text-[var(--stone-500)]">
            Begutachtung
          </legend>

          <div className="space-y-1.5">
            <label
              htmlFor="registration-reason"
              className="text-base font-medium text-[var(--stone-700)]"
            >
              Grund für die Begutachtung
            </label>
            <select
              id="registration-reason"
              required
              value={form.begutachtungsGrund}
              onChange={(e) =>
                updateField("begutachtungsGrund", e.target.value)
              }
              className={inputClass}
            >
              <option value="">Bitte auswählen...</option>
              {SELF_REGISTRATION_REASONS.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          </div>
        </fieldset>

        {/* --- Section: Einwilligungen --- */}
        <div className="mt-10 space-y-5 border-t-2 border-[var(--stone-100)] pt-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-[var(--stone-500)]">
            Einwilligungen
          </p>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 h-5 w-5 shrink-0 rounded border-2 border-[var(--stone-300)] accent-[var(--sage-600)]"
              checked={form.hasAcceptedEinwilligung}
              onChange={(e) =>
                updateField("hasAcceptedEinwilligung", e.target.checked)
              }
            />
            <span className="text-[var(--stone-700)] leading-relaxed">
              Ich habe die{" "}
              <a
                href={VIDEO_CONSENT_URL}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-[var(--sage-700)] underline underline-offset-2 decoration-1 hover:text-[var(--sage-800)]"
              >
                Einwilligungserklärung zur Videosprechstunde
              </a>{" "}
              gelesen. Ich willige ein, dass der Medizinische Dienst
              Berlin-Brandenburg mir per E-Mail eine Bestätigung sowie
              Termindaten und Zugangscodes zusendet.
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 h-5 w-5 shrink-0 rounded border-2 border-[var(--stone-300)] accent-[var(--sage-600)]"
              checked={form.hasAcceptedArt13Dsgvo}
              onChange={(e) =>
                updateField("hasAcceptedArt13Dsgvo", e.target.checked)
              }
            />
            <span className="text-[var(--stone-700)] leading-relaxed">
              Ich habe die{" "}
              <a
                href={ART13_DSGVO_URL}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-[var(--sage-700)] underline underline-offset-2 decoration-1 hover:text-[var(--sage-800)]"
              >
                Hinweise gemäß Art.&nbsp;13 DSGVO
              </a>{" "}
              zur Kenntnis genommen. Weitere Datenschutzinformationen:{" "}
              <a
                href="https://www.md-bb.org"
                target="_blank"
                rel="noreferrer"
                className="font-medium text-[var(--sage-700)] underline underline-offset-2 decoration-1 hover:text-[var(--sage-800)]"
              >
                www.md-bb.org
              </a>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 h-5 w-5 shrink-0 rounded border-2 border-[var(--stone-300)] accent-[var(--sage-600)]"
              checked={form.isHumanConfirmed}
              onChange={(e) =>
                updateField("isHumanConfirmed", e.target.checked)
              }
            />
            <span className="text-[var(--stone-700)] leading-relaxed">
              Ich bestätige, dass diese Anmeldung nicht automatisiert erfolgt.
            </span>
          </label>
        </div>

        {/* --- Submit --- */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="client-btn-primary mt-8 w-full disabled:opacity-60"
        >
          {isSubmitting ? "Wird registriert..." : "Registrieren"}
        </button>
      </form>
    </div>
  );
}
